import React, { useState, useEffect } from 'react';
import { Globe, MapPin, Mail, Phone, Share2, CheckCircle, AlertCircle, X } from 'lucide-react';

/**
 * LeadGeneralInfoSection — gedeelde "Overzicht/Algemeen" sectie met de
 * bedrijfs- en contactvelden van de lead-rij. Wordt gebruikt in de
 * detailweergaven van lead, prospect, onboarding en client zodat dezelfde
 * ingevulde data door de hele pipeline zichtbaar en bewerkbaar blijft.
 *
 * Data komt altijd vers van GET /api/v1/leads/{id} (volledige lead-rij) en
 * wordt per veld opgeslagen via PUT /api/v1/leads/{id} — zelfde pad als de
 * lead-popup. Bij 403 (bijv. finance read-only) verschijnt een nette melding.
 */

const getToken = () => sessionStorage.getItem('auth_token');

const FIELDS = [
  'company_name', 'company_website', 'company_country', 'company_industry',
  'partner_name', 'kvk_number', 'vat_number', 'client_iban',
  'address_street', 'address_city', 'address_postcode',
  'contact_name', 'contact_position', 'contact_email', 'contact_phone',
  'contact_mobile', 'communication_preference',
];

function pickFields(data) {
  const out = {};
  FIELDS.forEach(k => { out[k] = data?.[k] ?? ''; });
  return out;
}

export default function LeadGeneralInfoSection({ leadId, initial = {}, onSaved, className = '' }) {
  const [values, setValues] = useState(() => pickFields(initial));
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [msg, setMsg] = useState(null); // { type: 'success'|'error', text }

  // Haal de volledige lead-rij op zodat alle velden aanwezig zijn, ongeacht
  // welke (mogelijk beperkte) data de aanroepende weergave al had.
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/v1/leads/${leadId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled) setValues(pickFields(data));
      } catch { /* laat initial/lege waarden staan */ }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3000);
  };

  const saveField = async (apiField, value) => {
    try {
      const resp = await fetch(`/api/v1/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [apiField]: value }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || (resp.status === 403 ? 'Geen rechten om te bewerken' : `Fout ${resp.status}`));
      }
      setValues(v => ({ ...v, [apiField]: value }));
      showMsg('success', 'Opgeslagen');
      if (onSaved) onSaved(apiField, value);
    } catch (err) {
      showMsg('error', 'Opslaan mislukt: ' + err.message);
    }
    setEditingField(null);
  };

  const rowProps = { editingField, setEditingField, editValue, setEditValue, onSave: saveField };

  return (
    <div className={className}>
      {msg && (
        <div className={`mb-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
          msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-[#f0fdf4] text-[#16a34a]'
        }`}>
          {msg.type === 'error' ? <AlertCircle size={13} /> : <CheckCircle size={13} />}
          {msg.text}
        </div>
      )}
      <div className="grid grid-cols-2 gap-8">
        {/* Company Info */}
        <div>
          <h3 className="text-xs font-semibold text-[#566079] mb-3 uppercase tracking-wider">Bedrijf</h3>
          <div className="space-y-3">
            <EditableInfoRow label="Naam" value={values.company_name} apiField="company_name" {...rowProps} />
            <EditableInfoRow label="Website" value={values.company_website} apiField="company_website" icon={Globe} {...rowProps} />
            <EditableInfoRow label="Land" value={values.company_country} apiField="company_country" icon={MapPin} {...rowProps} />
            <EditableInfoRow label="Industrie" value={values.company_industry} apiField="company_industry" {...rowProps} />
            <EditableInfoRow label="Via Partner" value={values.partner_name} apiField="partner_name" icon={Share2} {...rowProps} />
            <EditableInfoRow label="KVK-nummer" value={values.kvk_number} apiField="kvk_number" {...rowProps} />
            <EditableInfoRow label="BTW-nummer" value={values.vat_number} apiField="vat_number" {...rowProps} />
            <EditableInfoRow label="Klant IBAN" value={values.client_iban} apiField="client_iban" {...rowProps} />
            <EditableInfoRow label="Adres" value={values.address_street} apiField="address_street" {...rowProps} />
            <EditableInfoRow label="Stad" value={values.address_city} apiField="address_city" {...rowProps} />
            <EditableInfoRow label="Postcode" value={values.address_postcode} apiField="address_postcode" {...rowProps} />
          </div>
        </div>
        {/* Contact Info */}
        <div>
          <h3 className="text-xs font-semibold text-[#566079] mb-3 uppercase tracking-wider">Contact</h3>
          <div className="space-y-3">
            <EditableInfoRow label="Naam" value={values.contact_name} apiField="contact_name" {...rowProps} />
            <EditableInfoRow label="Functie" value={values.contact_position} apiField="contact_position" {...rowProps} />
            <EditableInfoRow label="Email" value={values.contact_email} apiField="contact_email" icon={Mail} {...rowProps} />
            <EditableInfoRow label="Telefoon" value={values.contact_phone} apiField="contact_phone" icon={Phone} {...rowProps} />
            <EditableInfoRow label="Mobiel" value={values.contact_mobile} apiField="contact_mobile" icon={Phone} bold {...rowProps} />
            {/* Communicatievoorkeur */}
            <div className="flex items-center justify-between py-2 border-b border-[#f7f8fc]">
              <span className="text-xs font-medium" style={{ color: '#566079' }}>Voorkeur contact</span>
              <select
                value={values.communication_preference || ''}
                onChange={async (e) => { await saveField('communication_preference', e.target.value); }}
                className="text-xs rounded-lg px-2 py-1 border focus:outline-none"
                style={{ borderColor: '#e8eaf2', color: '#011745' }}>
                <option value="">— Selecteer —</option>
                <option value="email">E-mail</option>
                <option value="phone">Telefoon</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditableInfoRow({ label, value, apiField, icon: Icon, bold, editingField, setEditingField, editValue, setEditValue, onSave }) {
  const isEditing = editingField === apiField;

  if (isEditing) {
    return (
      <div>
        <p className="text-[10px] text-[#a4abbe] uppercase tracking-wider mb-0.5">{label}</p>
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') onSave(apiField, editValue);
              if (e.key === 'Escape') setEditingField(null);
            }}
            autoFocus
            className="flex-1 text-sm px-2 py-1 border border-[#3d61a4] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#3d61a4]"
            style={{ color: '#011745' }}
          />
          <button onClick={() => onSave(apiField, editValue)}
            className="p-1 rounded text-white" style={{ backgroundColor: '#3d61a4' }}>
            <CheckCircle size={14} />
          </button>
          <button onClick={() => setEditingField(null)}
            className="p-1 rounded text-[#7b859e] hover:bg-[#f3f4f8]">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group cursor-pointer" onClick={() => { setEditingField(apiField); setEditValue(value || ''); }}>
      <p className="text-[10px] text-[#a4abbe] uppercase tracking-wider mb-0.5">{label}</p>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-[#3d61a4]" />}
        <span className={`text-sm ${bold ? 'font-semibold' : ''}`} style={{ color: '#011745' }}>
          {value || <span className="text-[#cdd1e0] italic">Klik om in te vullen</span>}
        </span>
        <span className="opacity-0 group-hover:opacity-100 text-[10px] text-[#3d61a4] ml-auto transition-opacity">bewerken</span>
      </div>
    </div>
  );
}
